import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Loader2, BarChart3, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, ComposedChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { loadAusRepaymentPerformance } from "@/lib/api";
import { fmtAUD } from "./LoanRepaymentsAusParts";

const C = { gc: "#059669", capay: "#0d9488", payto: "#7c3aed", dd: "#0284c7", failed: "#dc2626", this: "#059669", last: "#94a3b8", rate: "#f59e0b", due: "#0ea5e9" };
const TABS = [
  ["dod", "Day on day"], ["wow", "Week on week"], ["mom", "Month on month"],
  ["week", "Across the week"], ["month", "Across the month"], ["scheme", "PayTo vs DD"], ["rate", "Collection rate"],
];
const k = (v) => (v == null ? "" : v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${Math.round(v)}`);
const pct = (v) => (v == null ? "—" : `${v}%`);
const money = (v) => (v == null ? "—" : fmtAUD(v));

function Tip({ active, payload, label, fmt = money }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded border bg-white px-2.5 py-1.5 text-[11px] shadow-md">
      <p className="font-semibold mb-0.5">{label}</p>
      {payload.filter((p) => p.value != null).map((p) => (
        <p key={p.dataKey} style={{ color: p.color || p.stroke || p.fill }}>{p.name}: <b>{(p.unit === "%" ? pct : fmt)(p.value)}</b></p>
      ))}
    </div>
  );
}

function Delta({ now, prev, ratioPct, testid }) {
  if (ratioPct == null && !prev) return null;
  const d = ratioPct != null ? Math.round(ratioPct - 100) : Math.round(((now - prev) / prev) * 100);
  return <span className={`text-[11px] font-semibold ${d >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid={testid}>{d >= 0 ? "▲" : "▼"} {Math.abs(d)}%</span>;
}

function Stat({ label, value, sub, delta }) {
  return (
    <div className="rounded border bg-white px-3 py-2 min-w-[130px]">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-base font-semibold tabular-nums">{value} {delta}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Chart({ tab, d }) {
  const axis = { tick: { fontSize: 10 }, tickLine: false, axisLine: false };
  if (tab === "dod") return (
    <ComposedChart data={d.daily} margin={{ left: 0, right: 8, top: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="label" {...axis} interval={4} /><YAxis {...axis} tickFormatter={k} width={44} />
      <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
      <Bar dataKey="gc" name="GoCardless" stackId="a" fill={C.gc} />
      <Bar dataKey="capay" name="Capay bank" stackId="a" fill={C.capay} radius={[3, 3, 0, 0]} />
      <Line dataKey="failed" name="Failed" stroke={C.failed} strokeWidth={1.5} dot={false} type="monotone" />
    </ComposedChart>
  );
  if (tab === "wow") return (
    <ComposedChart data={d.weekly} margin={{ left: 0, right: 8, top: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="label" {...axis} /><YAxis yAxisId="m" {...axis} tickFormatter={k} width={44} /><YAxis yAxisId="p" orientation="right" domain={[0, 100]} {...axis} tickFormatter={pct} width={36} />
      <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
      <Bar yAxisId="m" dataKey="total" name="Collected" fill={C.gc} radius={[3, 3, 0, 0]} />
      <Bar yAxisId="m" dataKey="failed" name="Failed" fill={C.failed} radius={[3, 3, 0, 0]} />
      <Line yAxisId="p" dataKey="success_rate" name="Success rate" unit="%" stroke={C.rate} strokeWidth={2} type="monotone" connectNulls />
    </ComposedChart>
  );
  if (tab === "mom") return (
    <ComposedChart data={d.monthly} margin={{ left: 0, right: 8, top: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="label" {...axis} /><YAxis yAxisId="m" {...axis} tickFormatter={k} width={44} /><YAxis yAxisId="p" orientation="right" domain={[0, 100]} {...axis} tickFormatter={pct} width={36} />
      <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
      <Bar yAxisId="m" dataKey="payto" name="PayTo" stackId="a" fill={C.payto} />
      <Bar yAxisId="m" dataKey="dd" name="Direct Debit" stackId="a" fill={C.dd} />
      <Bar yAxisId="m" dataKey="capay" name="Capay bank" stackId="a" fill={C.capay} radius={[3, 3, 0, 0]} />
      <Line yAxisId="p" dataKey="success_rate" name="Success rate" unit="%" stroke={C.rate} strokeWidth={2} type="monotone" connectNulls />
    </ComposedChart>
  );
  if (tab === "week") return (
    <BarChart data={d.weekday} margin={{ left: 0, right: 8, top: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="day" {...axis} /><YAxis {...axis} tickFormatter={k} width={44} />
      <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
      <Bar dataKey="last_week" name="Last week" fill={C.last} radius={[3, 3, 0, 0]} />
      <Bar dataKey="this_week" name="This week" fill={C.this} radius={[3, 3, 0, 0]} />
    </BarChart>
  );
  if (tab === "month") return (
    <LineChart data={d.mtd} margin={{ left: 0, right: 8, top: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="dom" {...axis} /><YAxis {...axis} tickFormatter={k} width={44} />
      <Tooltip content={<Tip />} labelFormatter={(v) => `Day ${v}`} /><Legend wrapperStyle={{ fontSize: 11 }} />
      <Line dataKey="last_month" name="Last month (cumulative)" stroke={C.last} strokeWidth={2} dot={false} type="monotone" />
      <Line dataKey="this_month" name="This month (cumulative)" stroke={C.this} strokeWidth={2.5} dot={false} type="monotone" />
    </LineChart>
  );
  if (tab === "scheme") return (
    <BarChart data={d.daily.filter((r) => r.payto || r.dd)} margin={{ left: 0, right: 8, top: 8 }} stackOffset="expand">
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="label" {...axis} interval={3} /><YAxis {...axis} tickFormatter={(v) => `${Math.round(v * 100)}%`} width={36} />
      <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
      <Bar dataKey="payto" name="PayTo" stackId="a" fill={C.payto} />
      <Bar dataKey="dd" name="Direct Debit" stackId="a" fill={C.dd} radius={[3, 3, 0, 0]} />
    </BarChart>
  );
  return (
    <ComposedChart data={d.rate} margin={{ left: 0, right: 8, top: 8 }}>
      <CartesianGrid strokeDasharray="3 3" vertical={false} />
      <XAxis dataKey="label" {...axis} /><YAxis yAxisId="m" {...axis} tickFormatter={k} width={44} /><YAxis yAxisId="p" orientation="right" domain={[0, 120]} {...axis} tickFormatter={pct} width={40} />
      <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
      <Area yAxisId="m" dataKey="due_total" name="LoanDisk due" fill={C.due} fillOpacity={0.12} stroke={C.due} strokeWidth={1} type="monotone" />
      <Bar yAxisId="m" dataKey="collected" name="Collected" fill={C.gc} radius={[3, 3, 0, 0]} />
      <Line yAxisId="p" dataKey="collection_rate" name="Collected ÷ due" unit="%" stroke={C.due} strokeWidth={2} type="monotone" connectNulls />
      <Line yAxisId="p" dataKey="success_rate" name="GC success rate" unit="%" stroke={C.rate} strokeWidth={2} strokeDasharray="4 3" type="monotone" connectNulls />
      <ReferenceLine yAxisId="p" y={100} stroke="#94a3b8" strokeDasharray="2 4" />
    </ComposedChart>
  );
}

const NOTE = {
  dod: "Confirmed money per day (GoCardless + Capay) with failed debits as a line. The last 2 days are still settling — BECS confirmations land 2 business days after the charge.",
  wow: "Collected vs failed per week (Mon–Sun) with the GC success rate (collected ÷ collected + failed).",
  mom: "Monthly totals split PayTo / Direct Debit / Capay bank, with the GC success rate.",
  week: "Same weekday this week vs last week — spot a soft Tuesday or a strong Monday.",
  month: "Cumulative month-to-date against the same day last month.",
  scheme: "Share of the day's GoCardless money that came via PayTo vs BECS Direct Debit.",
  rate: "Two lines: collected ÷ LoanDisk instalments due that day (true collection rate, last 14 days) and the GC success rate (collected ÷ collected + failed).",
};

export function AusRepaymentPerformanceSheet({ open, onOpenChange }) {
  const [tab, setTab] = useState("dod");
  const [basis, setBasis] = useState("charge");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    loadAusRepaymentPerformance(basis).then(setData).catch((e) => toast.error(e?.response?.data?.detail || "Could not load performance")).finally(() => setLoading(false));
  }, [open, basis]);

  const s = data?.summary;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[860px] sm:max-w-[860px] overflow-y-auto" data-testid="lra-performance-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2"><BarChart3 className="w-4 h-4" />Repayments performance <Badge className="bg-emerald-600 hover:bg-emerald-600 text-[10px]">AUS</Badge></SheetTitle>
          <SheetDescription>Mini view — the full Repayment Analysis replica comes later. {data?.data_from && `Data from ${data.data_from}.`}</SheetDescription>
        </SheetHeader>
        <div className="flex items-center justify-between gap-2 py-3 flex-wrap">
          <div className="flex rounded-md border overflow-hidden text-[11px]" data-testid="lra-perf-tabs">
            {TABS.map(([id, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`lra-perf-tab-${id}`}
                className={`px-2.5 py-1 transition ${tab === id ? "bg-emerald-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{label}</button>
            ))}
          </div>
          <div className="flex rounded-md border overflow-hidden text-[11px]" data-testid="lra-perf-basis">
            {[["charge", "Charge date"], ["confirmed", "Cleared date"]].map(([v, l]) => (
              <button key={v} onClick={() => setBasis(v)} data-testid={`lra-perf-basis-${v}`}
                className={`px-2.5 py-1 transition ${basis === v ? "bg-slate-800 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
        </div>
        {loading || !data ? (
          <div className="py-16 text-center text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Crunching the numbers…</div>
        ) : (
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap" data-testid="lra-perf-stats">
              <Stat label="Yesterday" value={money(s.yesterday.total)} sub={`${s.yesterday.count} payments · ${pct(s.yesterday.success_rate)} success`} />
              <Stat label="This week (so far)" value={money(s.this_week.total)} delta={<Delta now={s.this_week.total} prev={s.last_week.total} testid="lra-perf-wow-delta" />} sub={`last week ${money(s.last_week.total)}`} />
              <Stat label="Month to date" value={money(s.mtd)} delta={<Delta ratioPct={s.mtd_vs_last_month_same_day} testid="lra-perf-mtd-delta" />} sub={`vs same day last month · last month ${money(s.last_month_full)} total`} />
              <Stat label="Success rate (span)" value={pct(s.span_success_rate)} sub="collected ÷ (collected + failed)" />
              <Stat label="PayTo share (month)" value={pct(s.this_month.payto_share)} sub={`DD ${pct(s.this_month.dd_share)}`} />
            </div>
            <div className="rounded border bg-white p-2" data-testid={`lra-perf-chart-${tab}`}>
              <ResponsiveContainer width="100%" height={300}>
                <Chart tab={tab} d={data} />
              </ResponsiveContainer>
            </div>
            <p className="text-[11px] text-muted-foreground flex items-start gap-1.5"><RefreshCw className="w-3 h-3 mt-0.5 shrink-0" />{NOTE[tab]} Basis: {basis === "charge" ? "charge date" : "cleared date"} · {data.payments_seen} payments in view.</p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
